import { Alert, Button } from "@mui/material";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../store";
import { fetchTeacherInvitations } from "../store/studentsSlice";

interface Props {
  showBanner: boolean;
}

export default function IncomingTeacherInvitations({
  showBanner,
}: Props): React.ReactElement | null {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("texts");
  const { pathname } = useLocation();
  const user = useAppSelector((state) => state.auth.user);
  const relationships = useAppSelector(
    (state) => state.students.student.relationships,
  );
  useEffect((): (() => void) | undefined => {
    if (!user?.emailVerified || user.isAnonymous) return;
    const refresh = (): void => {
      if (!document.hidden) void dispatch(fetchTeacherInvitations());
    };
    refresh();
    const interval = window.setInterval(refresh, 60_000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return (): void => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [dispatch, user?.id, user?.emailVerified, user?.isAnonymous]);
  const count = relationships.filter(
    (entry): boolean => entry.status === "INVITED",
  ).length;
  if (
    !showBanner ||
    !count ||
    pathname === "/students" ||
    pathname.startsWith("/teacher-invitations/")
  )
    return null;
  return (
    <Alert
      severity="warning"
      sx={{
        mb: 2,
        border: 1,
        borderColor: "error.main",
        borderLeftWidth: 5,
        bgcolor: "background.paper",
        color: "text.primary",
        "& .MuiAlert-icon": { color: "error.main" },
        alignItems: "center",
      }}
      action={
        <Button
          component={Link}
          to="/students?role=student"
          size="small"
          variant="contained"
          color="error"
        >
          {t("students.reviewInvitations")}
        </Button>
      }
    >
      {t("students.incomingInvitations", { count })}
    </Alert>
  );
}
